/**
 * Area `party-confirmation` of the console gateway — over `apps/api/src/routes/party-confirmation/`. Owned by plan-029
 * unit **WAPP-CONF**: "Confirm who's calling" — what a VoiceAgent protects and how a caller confirms who they are
 * (design §40.8.2, §40.19.5, §40.25.1; ADR 0082, proposed; glossary §6.44 *Caller confirmation*, *Protected capability*).
 *
 * ```
 *   readVoiceAgentPartyConfirmation     `getVoiceAgentPartyConfirmation`   GET /v1/agents/{id}/party-confirmation  ETag = revision
 *   changeVoiceAgentPartyConfirmation   `setVoiceAgentPartyConfirmation`   PUT /v1/agents/{id}/party-confirmation  If-Match required
 * ```
 *
 * THE PRICE IS READ, NEVER WRITTEN HERE (design §40.25.1). `confirmationCodePrice` is what one text message code costs
 * the organization now (its own price, else the standard) with the next scheduled change, in minor units and a
 * currency; `null` while no price is set, and the SMS code methods are then unavailable. The panel formats it with
 * `formatMoney`; no figure appears in this source.
 *
 * Refusals raise `PartyRefusedError` (from `./caller-protection.ts`) with the rule a `422` broke in `detailCode`
 * (`capability_not_protectable` names `detail.capability`; `method_unavailable` names `detail.reason`).
 *
 * The register is EMPTY: both operations are in the assembled spec. The sandbox half is
 * `../sandbox/party-confirmation.ts`.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { type WeTalkApiClient } from "@wetalk/api-client";
import {
  PARTY_CONFIRMATION_MINIMUM,
  PROTECTED_CAPABILITY,
  type CurrencyCode,
  type PartyConfirmationMinimum,
  type ProtectedCapability,
} from "@wetalk/domain";

import type { ConsoleGateway } from "../gateway.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import { useGateway } from "../provider.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";
import { partyBodyOf } from "./caller-protection.js";

export { PARTY_CONFIRMATION_MINIMUM, PROTECTED_CAPABILITY, type PartyConfirmationMinimum, type ProtectedCapability };

/** One price: minor units and a currency, and the instant it takes (or took) effect. */
export interface ConfirmationCodePriceStepView {
  readonly amountMinor: number;
  readonly currency: CurrencyCode;
  readonly effectiveFrom: string;
}

/** The price in force and the next change (`ConfirmationCodePrice`). */
export interface ConfirmationCodePriceView extends ConfirmationCodePriceStepView {
  readonly next: ConfirmationCodePriceStepView | null;
}

/** "Confirm who's calling" (`PartyConfirmationSetting`). */
export interface PartyConfirmationSettingView {
  /** `false` while confirming who's calling is not available yet in this deploy: the values show, nothing can change. */
  readonly available: boolean;
  readonly revision: number;
  /** What the agent does only for a confirmed caller. Empty: confirmation is off. */
  readonly protectedCapability: readonly ProtectedCapability[];
  /** `null` while nothing is saved. */
  readonly minimumMethod: PartyConfirmationMinimum | null;
  /** What this kind of agent can protect at all (the agent_template's declaration). */
  readonly protectableCapability: readonly ProtectedCapability[];
  /** Which ways of confirming are available now. */
  readonly methodAvailability: { readonly smsCode: boolean; readonly signedVisitor: boolean; readonly passcode: boolean };
  /** One code's price, or `null` while none is set. */
  readonly confirmationCodePrice: ConfirmationCodePriceView | null;
  readonly updatedAt: string | null;
}

/** A change: every field stated (`PartyConfirmationChange`). */
export interface PartyConfirmationChange {
  readonly protectedCapability: readonly ProtectedCapability[];
  readonly minimumMethod: PartyConfirmationMinimum;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** plan-029 API-CONF: "Confirm who's calling" with its revision, the declaration, the methods and the code price. */
    readVoiceAgentPartyConfirmation(voiceAgentId: string): Promise<PartyConfirmationSettingView>;
    /**
     * plan-029 API-CONF: save it, naming the revision read. Raises `PartyRefusedError`: `party_confirmation_unavailable`,
     * `capability_not_protectable`, `method_unavailable`, `confirmation_code_price_unset`, `409 revision_stale`.
     */
    changeVoiceAgentPartyConfirmation(
      voiceAgentId: string,
      change: PartyConfirmationChange,
      revision: number,
    ): Promise<PartyConfirmationSettingView>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type PartyConfirmationGatewaySlice = Pick<ConsoleGateway, "readVoiceAgentPartyConfirmation" | "changeVoiceAgentPartyConfirmation">;

/** This area's rows of `GATEWAY_GAP`: none — both operations are served. */
export const partyConfirmationGap: readonly GatewayGap[] = [];

// ── the wire ─────────────────────────────────────────────────────────────────────────────────────────────

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, allowed.join(" | "), raw);
  return found;
}

function capabilitiesOf(value: unknown, path: string): readonly ProtectedCapability[] {
  return readArray(value, path).map((one, index) => oneOf(PROTECTED_CAPABILITY, one, `${path}[${String(index)}]`));
}

function priceStepOf(wire: unknown, path: string): ConfirmationCodePriceStepView {
  const step = readRecord(wire, path);
  return {
    amountMinor: readWholeNumber(step["amount_minor"], `${path}.amount_minor`),
    currency: oneOf(["EUR"] as const, step["currency"], `${path}.currency`),
    effectiveFrom: readText(step["effective_from"], `${path}.effective_from`),
  };
}

/** One `PartyConfirmationSetting`. */
export function partyConfirmationSettingOf(wire: unknown, path: string): PartyConfirmationSettingView {
  const data = readRecord(wire, path);
  const availability = readRecord(data["method_availability"], `${path}.method_availability`);
  const price = data["confirmation_code_price"];
  return {
    available: readBoolean(data["available"], `${path}.available`),
    revision: readWholeNumber(data["revision"], `${path}.revision`),
    protectedCapability: capabilitiesOf(data["protected_capability"], `${path}.protected_capability`),
    minimumMethod:
      data["minimum_method"] === null ? null : oneOf(PARTY_CONFIRMATION_MINIMUM, data["minimum_method"], `${path}.minimum_method`),
    protectableCapability: capabilitiesOf(data["protectable_capability"], `${path}.protectable_capability`),
    methodAvailability: {
      smsCode: readBoolean(availability["sms_code"], `${path}.method_availability.sms_code`),
      signedVisitor: readBoolean(availability["signed_visitor"], `${path}.method_availability.signed_visitor`),
      passcode: readBoolean(availability["passcode"], `${path}.method_availability.passcode`),
    },
    confirmationCodePrice:
      price === null
        ? null
        : {
            ...priceStepOf(price, `${path}.confirmation_code_price`),
            next:
              readRecord(price, `${path}.confirmation_code_price`)["next"] === null
                ? null
                : priceStepOf(readRecord(price, `${path}.confirmation_code_price`)["next"], `${path}.confirmation_code_price.next`),
          },
    updatedAt: data["updated_at"] === null ? null : readText(data["updated_at"], `${path}.updated_at`),
  };
}

function dataOf(body: unknown, operationId: string): unknown {
  return readRecord(body, operationId)["data"];
}

export function partyConfirmationApiSlice(client: WeTalkApiClient, _context: SliceContext): PartyConfirmationGatewaySlice {
  return {
    async readVoiceAgentPartyConfirmation(voiceAgentId) {
      const answer = await client.getVoiceAgentPartyConfirmation({ path: { voice_agent_id: voiceAgentId } });
      return partyConfirmationSettingOf(dataOf(partyBodyOf(answer), "getVoiceAgentPartyConfirmation"), "getVoiceAgentPartyConfirmation.data");
    },
    async changeVoiceAgentPartyConfirmation(voiceAgentId, change, revision) {
      const answer = await client.setVoiceAgentPartyConfirmation({
        path: { voice_agent_id: voiceAgentId },
        body: { protected_capability: [...change.protectedCapability], minimum_method: change.minimumMethod },
        header: { "if-match": `"${String(revision)}"` },
      });
      return partyConfirmationSettingOf(dataOf(partyBodyOf(answer), "setVoiceAgentPartyConfirmation"), "setVoiceAgentPartyConfirmation.data");
    },
  };
}

// ── the reads the screens use ────────────────────────────────────────────────────────────────────────────

/** Query keys of this area. */
export const PARTY_CONFIRMATION_QUERY_KEY = {
  setting: (voiceAgentId: string) => ["party-confirmation", voiceAgentId] as const,
};

/** "Confirm who's calling". A refused or failed read leaves `data` undefined — never a remembered value. */
export function useVoiceAgentPartyConfirmation(voiceAgentId: string): UseQueryResult<PartyConfirmationSettingView> {
  const gateway = useGateway();
  return useQuery({
    queryKey: PARTY_CONFIRMATION_QUERY_KEY.setting(voiceAgentId),
    queryFn: () => gateway.readVoiceAgentPartyConfirmation(voiceAgentId),
    retry: false,
  });
}
